import type { Maybe } from '@adornis/base/utilTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Subscription } from '@adornis/baseql/decorators.js';
import { getCollectionHandle } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { KPF } from '../master-data-management/kpf/entities/KPF.js';
import { ExternalContactPerson } from '../master-data-management/users/entities/ExternalContactPerson.js';
import { ContextPrefix } from '../permissions.js';
import { SemanticRelationship } from './SemanticRelationship.js';

/**
 * @entity ExternalKPFRelationship
 */
@Entity()
export class ExternalKPFRelationship extends SemanticRelationship {
  public static override _class = 'ExternalKPFRelationship';

  @DataLoadedField(() => ExternalContactPerson, ({ subjectID }) => subjectID)
  public override subject: Maybe<ExternalContactPerson>;

  @DataLoadedField(() => KPF, ({ objectID }) => objectID)
  public override object: Maybe<KPF>;

  public override getContext() {
    return this.objectID === GLOBAL_CONTEXT ? GLOBAL_CONTEXT : ContextPrefix.KPF + this.objectID;
  }

  // TODO abstract this into superclass
  @Subscription(() => [ExternalKPFRelationship])
  static subscribeExternalKPFRelationsForKPF(@Arg('kpfID', () => String) kpfID: string) {
    return (gqlFields: BaseQLSelectionSet<ExternalKPFRelationship>) => {
      const selector = { _class: this._class, objectID: kpfID };
      const handle = getCollectionHandle<ExternalKPFRelationship>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as ExternalKPFRelationship[]);
        }),
      );
    };
  }

  // TODO abstract this into superclass
  @Subscription(() => [ExternalKPFRelationship])
  static subscribeExternalKPFRelationsForUser(@Arg('userID', () => String) userID: string) {
    return (gqlFields: BaseQLSelectionSet<ExternalKPFRelationship>) => {
      const selector = { _class: this._class, subjectID: userID };
      const handle = getCollectionHandle<ExternalKPFRelationship>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as ExternalKPFRelationship[]);
        }),
      );
    };
  }
}
